import React, { useState, useEffect } from 'react';
import { initializeCognito, signIn, signOut, getCurrentUser, getIdToken } from './auth';
import './App.css';

const MODELS = [
  { key: 'claude-3-haiku', name: 'Claude 3 Haiku', description: 'Fast and cost-effective (no caching)', supportsCache: false },
  { key: 'claude-sonnet-4.6', name: 'Claude Sonnet 4.6', description: 'Balanced performance', supportsCache: true },
  { key: 'claude-opus-4.6', name: 'Claude Opus 4.6', description: 'Most capable', supportsCache: true },
  { key: 'nova-pro', name: 'Amazon Nova Pro', description: 'Powerful multimodal model', supportsCache: true },
  { key: 'nova-lite', name: 'Amazon Nova Lite', description: 'Fast and affordable', supportsCache: true },
  { key: 'gpt-oss-20b', name: 'GPT OSS 20B', description: 'Open source model', supportsCache: false },
  { key: 'tinyllama-1-1b-chat', name: 'TinyLlama 1.1B Chat', description: 'Custom imported model (1.1B params)', supportsCache: false }
];

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [selectedModel, setSelectedModel] = useState('claude-sonnet-4.6');
  const [document, setDocument] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [cacheStats, setCacheStats] = useState({
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    totalCachedLatency: 0,
    totalUncachedLatency: 0,
    cachedRequestCount: 0,
    uncachedRequestCount: 0,
    totalSavings: 0
  });

  useEffect(() => {
    // Load configuration
    fetch('/config.json')
      .then(res => res.json())
      .then(cfg => {
        setConfig(cfg);
        initializeCognito(cfg.userPoolId, cfg.clientId);

        // Check if user is already logged in
        const currentUser = getCurrentUser();
        if (currentUser) {
          currentUser.getSession((err, session) => {
            if (!err && session.isValid()) {
              setIsAuthenticated(true);
            }
            setIsLoading(false);
          });
        } else {
          setIsLoading(false);
        }
      })
      .catch(err => {
        console.error('Failed to load config:', err);
        setError('Failed to load application configuration');
        setIsLoading(false);
      });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      setIsAuthenticated(true);
      setEmail('');
      setPassword('');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    signOut();
    setIsAuthenticated(false);
    setMessages([]);
    setPrompt('');
    setDocument(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    setIsUploading(true);

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64Data = event.target.result.split(',')[1];
          const idToken = await getIdToken();

          const response = await fetch(config.uploadEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
              file: base64Data,
              fileName: file.name,
              sessionId: sessionId
            })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Failed to upload document');
          }

          setDocument({
            fileName: data.fileName,
            numPages: data.numPages,
            textLength: data.textLength
          });

          setMessages(prev => [...prev, {
            role: 'system',
            content: `📄 Document uploaded: ${data.fileName} (${data.numPages} pages). You can now ask questions about this document.`
          }]);

        } catch (err) {
          setError(err.message || 'Failed to upload document');
        } finally {
          setIsUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError(err.message || 'Failed to read file');
      setIsUploading(false);
    }
  };

  const clearDocument = () => {
    setDocument(null);
    setCacheStats({
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      totalCachedLatency: 0,
      totalUncachedLatency: 0,
      cachedRequestCount: 0,
      uncachedRequestCount: 0,
      totalSavings: 0
    });
    setMessages(prev => [...prev, {
      role: 'system',
      content: '📄 Document context cleared. Cache statistics reset.'
    }]);
  };

  const handleSubmitPrompt = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setError('');
    setIsSubmitting(true);

    const userMessage = { role: 'user', content: prompt, model: selectedModel };
    setMessages(prev => [...prev, userMessage]);
    setPrompt('');

    try {
      const idToken = await getIdToken();

      // Build conversation history (exclude system messages)
      const conversationHistory = messages
        .filter(m => m.role !== 'system')
        .map(m => ({
          role: m.role,
          content: m.content
        }));

      const response = await fetch(config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          prompt: userMessage.content,
          model: selectedModel,
          sessionId: sessionId,
          conversationHistory: conversationHistory
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get response');
      }

      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        model: data.model,
        modelKey: data.modelKey,
        usage: data.usage,
        metrics: data.metrics,
        cost: data.cost,
        caching: data.caching,
        hasDocument: data.hasDocument
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Update cache statistics
      if (data.caching && data.caching.enabled) {
        setCacheStats(prev => {
          const isHit = data.caching.cacheHit;
          const latency = data.metrics.latencyMs || 0;
          const savings = data.cost.cacheSavings || 0;

          return {
            totalRequests: prev.totalRequests + 1,
            cacheHits: prev.cacheHits + (isHit ? 1 : 0),
            cacheMisses: prev.cacheMisses + (isHit ? 0 : 1),
            totalCachedLatency: prev.totalCachedLatency + (isHit ? latency : 0),
            totalUncachedLatency: prev.totalUncachedLatency + (isHit ? 0 : latency),
            cachedRequestCount: prev.cachedRequestCount + (isHit ? 1 : 0),
            uncachedRequestCount: prev.uncachedRequestCount + (isHit ? 0 : 1),
            totalSavings: prev.totalSavings + savings
          };
        });
      }

    } catch (err) {
      setError(err.message || 'Failed to send message');
      // Remove the user message if request failed
      setMessages(prev => prev.slice(0, -1));
      setPrompt(userMessage.content); // Restore the prompt
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container">
        <div className="auth-card">
          <h1>GenAI Chat</h1>
          <p className="subtitle">Powered by Amazon Bedrock</p>

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isSubmitting}
              />
            </div>

            {error && <div className="error">{error}</div>}

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const selectedModelInfo = MODELS.find(m => m.key === selectedModel);

  return (
    <div className="container">
      <div className="chat-container">
        <div className="chat-header">
          <div>
            <h1>GenAI Chat</h1>
            <p className="subtitle">Multi-Model Comparison via Amazon Bedrock</p>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            Sign Out
          </button>
        </div>

        <div className="controls-section">
          <div className="model-selector">
            <label htmlFor="model-select">Select Model:</label>
            <select
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={isSubmitting}
            >
              {MODELS.map(model => (
                <option key={model.key} value={model.key}>
                  {model.name} - {model.description}
                </option>
              ))}
            </select>
            {selectedModelInfo.supportsCache && (
              <span className="cache-badge">🔄 Prompt Caching</span>
            )}
          </div>

          <div className="document-section">
            {!document ? (
              <div className="upload-area">
                <label htmlFor="file-upload" className="upload-label">
                  📄 Upload PDF (max 20 pages)
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload" className="upload-button">
                  {isUploading ? 'Uploading...' : 'Choose File'}
                </label>
              </div>
            ) : (
              <div className="document-info">
                <div className="doc-status">
                  <span className="doc-icon">📄</span>
                  <div className="doc-details">
                    <strong>{document.fileName}</strong>
                    <span className="doc-meta">{document.numPages} pages</span>
                  </div>
                  <button onClick={clearDocument} className="clear-doc-btn" disabled={isSubmitting}>
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {document && cacheStats.totalRequests > 0 && selectedModelInfo.supportsCache && (
          <div className="cache-stats-panel">
            <h3>📊 Prompt Caching Statistics</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Cache Hit Rate</div>
                <div className="stat-value">
                  {cacheStats.totalRequests > 0
                    ? `${Math.round((cacheStats.cacheHits / cacheStats.totalRequests) * 100)}%`
                    : '0%'}
                </div>
                <div className="stat-detail">
                  {cacheStats.cacheHits} hits / {cacheStats.cacheMisses} misses
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Avg Latency</div>
                <div className="stat-value-comparison">
                  <div className="latency-cached">
                    <span className="latency-label">Cached:</span>
                    <span className="latency-number">
                      {cacheStats.cachedRequestCount > 0
                        ? `${Math.round(cacheStats.totalCachedLatency / cacheStats.cachedRequestCount)}ms`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="latency-uncached">
                    <span className="latency-label">Uncached:</span>
                    <span className="latency-number">
                      {cacheStats.uncachedRequestCount > 0
                        ? `${Math.round(cacheStats.totalUncachedLatency / cacheStats.uncachedRequestCount)}ms`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                {cacheStats.cachedRequestCount > 0 && cacheStats.uncachedRequestCount > 0 && (
                  <div className="stat-detail">
                    {Math.round(
                      ((cacheStats.totalUncachedLatency / cacheStats.uncachedRequestCount) -
                       (cacheStats.totalCachedLatency / cacheStats.cachedRequestCount)) /
                      (cacheStats.totalUncachedLatency / cacheStats.uncachedRequestCount) * 100
                    )}% faster with cache
                  </div>
                )}
              </div>

              <div className="stat-card">
                <div className="stat-label">Total Savings</div>
                <div className="stat-value savings">
                  ${(cacheStats.totalSavings * 1000).toFixed(4)}
                </div>
                <div className="stat-detail">per 1K requests</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Total Requests</div>
                <div className="stat-value">{cacheStats.totalRequests}</div>
                <div className="stat-detail">
                  {document.fileName} ({document.numPages} pages)
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="empty-state">
              <h2>Welcome to GenAI Chat!</h2>
              <p>Select a model and ask anything to get started.</p>
              <p className="model-info">Currently selected: <strong>{selectedModelInfo.name}</strong></p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role}`}>
                <div className="message-role">
                  {msg.role === 'user' ? '👤 You' : msg.role === 'system' ? '📋 System' : '🤖 Assistant'}
                  {msg.model && ` (${msg.model})`}
                </div>
                <div className="message-content">{msg.content}</div>
                {msg.usage && (
                  <div className="message-metrics">
                    <div className="metrics-grid">
                      <div className="metric">
                        <span className="metric-label">Tokens:</span>
                        <span className="metric-value">
                          {msg.usage.inputTokens} in / {msg.usage.outputTokens} out ({msg.usage.totalTokens} total)
                        </span>
                      </div>
                      {msg.metrics && (
                        <div className="metric">
                          <span className="metric-label">Latency:</span>
                          <span className="metric-value">
                            {Math.round(msg.metrics.latencyMs)}ms
                          </span>
                        </div>
                      )}
                      {msg.cost && (
                        <div className="metric">
                          <span className="metric-label">Cost:</span>
                          <span className="metric-value">
                            ${(msg.cost.totalCost * 1000).toFixed(4)} (per 1K requests)
                          </span>
                        </div>
                      )}
                    </div>
                    {msg.caching && msg.caching.enabled && (
                      <div className="cache-metrics">
                        <div className="cache-status">
                          {msg.caching.cacheHit ? (
                            <span className="cache-hit">✓ Cache Hit</span>
                          ) : (
                            <span className="cache-miss">⚡ Cache Created</span>
                          )}
                        </div>
                        <div className="cache-details">
                          {msg.caching.cacheReadInputTokens > 0 && (
                            <span className="cache-stat">
                              📖 Read: {msg.caching.cacheReadInputTokens} tokens
                            </span>
                          )}
                          {msg.caching.cacheCreationInputTokens > 0 && (
                            <span className="cache-stat">
                              ✍️ Created: {msg.caching.cacheCreationInputTokens} tokens
                            </span>
                          )}
                          {msg.cost.cacheSavings > 0 && (
                            <span className="cache-savings">
                              💰 Saved: ${(msg.cost.cacheSavings * 1000).toFixed(4)} (per 1K requests)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
          {isSubmitting && (
            <div className="message assistant">
              <div className="message-role">🤖 Assistant ({selectedModelInfo.name})</div>
              <div className="message-content typing">Thinking...</div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmitPrompt} className="input-form">
          {error && <div className="error">{error}</div>}
          <div className="input-group">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Type your message..."
              disabled={isSubmitting}
              autoFocus
            />
            <button type="submit" disabled={isSubmitting || !prompt.trim()}>
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default App;
